/**
 * CBO Network View Component
 * Shows CBO's programs, participating partners, and network collaboration opportunities
 * Trust Marketplace - Phase 4: Network Management System
 */

import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { getWeaveinApiUrl } from '@/src/config';
import { getActiveCboId } from '@/services/api/organizations';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { authFetch } from '@/lib/authFetch';
import {
  Building2,
  Users,
  TrendingUp,
  Award,
  Target,
  Network,
  Share2,
  DollarSign,
  Calendar,
  MapPin,
  ExternalLink,
  Eye,
  MessageSquare
} from 'lucide-react';

interface CBONetworkViewProps {
  cboId?: number;
  cboName?: string;
}

interface ProgramParticipation {
  id: number;
  program_id: number;
  program_name: string;
  sponsor_id: number;
  sponsor_name: string;
  participation_status: string;
  join_date: string;
  enrolled_members_count: number;
  incentives_earned: number;
  performance_score?: number;
  visible_to_network: boolean;
}

interface NetworkCBO {
  cbo_id: number;
  cbo_name: string;
  shared_programs: number;
  total_enrollments: number;
  performance_rank?: number;
  collaboration_opportunities?: string[];
  contact_email?: string;
  service_area?: string;
}

interface SharedResource {
  id: number;
  resource_name: string;
  resource_type: string;
  shared_by_cbo_id: number;
  shared_by_cbo_name: string;
  resource_description?: string;
  access_level: string;
  created_at: string;
}

const CBONetworkView: React.FC<CBONetworkViewProps> = ({ cboId: cboIdProp, cboName }) => {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [myPrograms, setMyPrograms] = useState<ProgramParticipation[]>([]);
  const [networkCBOs, setNetworkCBOs] = useState<NetworkCBO[]>([]);
  const [sharedResources, setSharedResources] = useState<SharedResource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedProgram, setSelectedProgram] = useState<string>('all');
  const [selectedCBO, setSelectedCBO] = useState<NetworkCBO | null>(null);

  // Get CBO ID from prop or localStorage
  const cboId = cboIdProp || getActiveCboId();

  useEffect(() => {
    if (cboId) {
      fetchNetworkData();
    } else {
      setError('No organization selected. Please select an organization from the header.');
      setLoading(false);
    }
  }, [cboId, selectedProgram]);

  const fetchNetworkData = async () => {
    try {
      setLoading(true);

      // Fetch all network data in parallel
      const [programsRes, cbosRes, resourcesRes] = await Promise.all([
        authFetch(getWeaveinApiUrl(`/api/program-cbo-participants/cbo/${cboId}`)),
        authFetch(getWeaveinApiUrl(`/api/cbo-network-view/${cboId}`)),
        authFetch(getWeaveinApiUrl(`/api/cbo-shared-resources?cbo_id=${cboId}`))
      ]);

      if (!programsRes.ok || !cbosRes.ok || !resourcesRes.ok) {
        throw new Error('Failed to fetch network data');
      }

      const [programsData, cbosData, resourcesData] = await Promise.all([
        programsRes.json(),
        cbosRes.json(),
        resourcesRes.json()
      ]);

      setMyPrograms(programsData);
      setNetworkCBOs(cbosData);
      setSharedResources(resourcesData);
    } catch (err) {
      console.error('Error fetching network data:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string): string => {
    switch (status.toUpperCase()) {
      case 'ACTIVE':
      case 'APPROVED':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'INVITED':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'INACTIVE':
      case 'SUSPENDED':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return fmt.medium(dateString);
  };

  const filteredPrograms = selectedProgram === 'all'
    ? myPrograms
    : myPrograms.filter(p => p.program_id.toString() === selectedProgram);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="p-6 bg-red-50 border-red-200">
        <p className="text-red-800">Error: {error}</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Network className="w-7 h-7 text-blue-600" />
          {t('network.my_network', 'My Network')}
        </h2>
        {cboName && (
          <p className="text-sm text-gray-600 mt-1">{cboName}</p>
        )}
      </div>

      {/* Network Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4 bg-blue-50 border-blue-200">
          <div className="flex items-center gap-2 mb-1">
            <Target className="w-4 h-4 text-blue-600" />
            <span className="text-xs text-blue-700 font-medium">{t('network.my_programs', 'My Programs')}</span>
          </div>
          <div className="text-2xl font-bold text-blue-900">{myPrograms.length}</div>
          <div className="text-xs text-blue-600 mt-1">
            {myPrograms.filter(p => p.participation_status === 'ACTIVE').length} active
          </div>
        </Card>

        <Card className="p-4 bg-green-50 border-green-200">
          <div className="flex items-center gap-2 mb-1">
            <Users className="w-4 h-4 text-green-600" />
            <span className="text-xs text-green-700 font-medium">{t('network.total_enrollments', 'Total Enrollments')}</span>
          </div>
          <div className="text-2xl font-bold text-green-900">
            {myPrograms.reduce((sum, p) => sum + p.enrolled_members_count, 0)}
          </div>
          <div className="text-xs text-green-600 mt-1">across all programs</div>
        </Card>

        <Card className="p-4 bg-purple-50 border-purple-200">
          <div className="flex items-center gap-2 mb-1">
            <DollarSign className="w-4 h-4 text-purple-600" />
            <span className="text-xs text-purple-700 font-medium">{t('network.incentives_earned', 'Incentives Earned')}</span>
          </div>
          <div className="text-2xl font-bold text-purple-900">
            {formatCurrency(myPrograms.reduce((sum, p) => sum + p.incentives_earned, 0))}
          </div>
          <div className="text-xs text-purple-600 mt-1">total earnings</div>
        </Card>

        <Card className="p-4 bg-yellow-50 border-yellow-200">
          <div className="flex items-center gap-2 mb-1">
            <Building2 className="w-4 h-4 text-yellow-600" />
            <span className="text-xs text-yellow-700 font-medium">{t('network.partner_cbos', 'Partner CBOs')}</span>
          </div>
          <div className="text-2xl font-bold text-yellow-900">{networkCBOs.length}</div>
          <div className="text-xs text-yellow-600 mt-1">in network</div>
        </Card>
      </div>

      {/* My Programs Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Target className="w-5 h-5 text-blue-600" />
            {t('network.my_programs_1', 'My Programs')}
          </h3>
          <div className="w-64">
            <Select value={selectedProgram} onValueChange={setSelectedProgram}>
              <SelectTrigger>
                <SelectValue placeholder={t('network.all_programs_1', 'All Programs')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('network.all_programs', 'All Programs')}</SelectItem>
                {myPrograms.map(p => (
                  <SelectItem key={p.program_id} value={p.program_id.toString()}>
                    {p.program_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredPrograms.map(program => (
            <Card key={program.id} className="p-5 hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 mb-1">{program.program_name}</h4>
                  <p className="text-xs text-gray-500">Sponsor: {program.sponsor_name}</p>
                </div>
                <Badge className={getStatusColor(program.participation_status)}>
                  {program.participation_status}
                </Badge>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-4">
                <div>
                  <div className="text-xs text-gray-500">{t('network.enrolled_members', 'Enrolled Members')}</div>
                  <div className="text-lg font-bold text-gray-900">{program.enrolled_members_count}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">{t('network.incentives_earned_1', 'Incentives Earned')}</div>
                  <div className="text-lg font-bold text-green-600">
                    {formatCurrency(program.incentives_earned)}
                  </div>
                </div>
              </div>

              {program.performance_score !== undefined && (
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-500">{t('network.performance_score', 'Performance Score')}</span>
                    <span className="text-xs font-semibold text-blue-600">{program.performance_score}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full"
                      style={{ width: `${program.performance_score}%` }}
                    ></div>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between text-xs text-gray-500 pt-3 border-t">
                <div className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  Joined {formatDate(program.join_date)}
                </div>
                {program.visible_to_network && (
                  <Badge variant="outline" className="text-xs">
                    <Eye className="w-3 h-3 mr-1" />
                    {t('network.visible', 'Visible')}
                  </Badge>
                )}
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Partner CBOs Section */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
          <Building2 className="w-5 h-5 text-blue-600" />
          Partner CBOs ({networkCBOs.length})
        </h3>

        {networkCBOs.length === 0 ? (
          <Card className="p-12 text-center">
            <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h4 className="text-lg font-semibold text-gray-900 mb-2">{t('network.no_partner_cbos_yet', 'No partner CBOs yet')}</h4>
            <p className="text-sm text-gray-600">
              {t('network.partner_cbos_will_appear_here', 'Partner CBOs will appear here once you join shared programs')}
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {networkCBOs.map(cbo => (
              <Card key={cbo.cbo_id} className="p-4 hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-gray-900 truncate mb-1">{cbo.cbo_name}</h4>
                    {cbo.service_area && (
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <MapPin className="w-3 h-3" />
                        {cbo.service_area}
                      </div>
                    )}
                  </div>
                  {cbo.performance_rank && (
                    <Badge className="bg-yellow-100 text-yellow-800 flex-shrink-0">
                      #{cbo.performance_rank}
                    </Badge>
                  )}
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">{t('network.shared_programs', 'Shared Programs')}</span>
                    <span className="font-semibold text-gray-900">{cbo.shared_programs}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">{t('network.total_enrollments_1', 'Total Enrollments')}</span>
                    <span className="font-semibold text-gray-900">{cbo.total_enrollments}</span>
                  </div>
                </div>

                {cbo.collaboration_opportunities && cbo.collaboration_opportunities.length > 0 && (
                  <div className="mb-4">
                    <div className="text-xs text-gray-500 mb-1">{t('network.opportunities', 'Opportunities')}</div>
                    <div className="flex flex-wrap gap-1">
                      {cbo.collaboration_opportunities.slice(0, 2).map((opp, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {opp}
                        </Badge>
                      ))}
                      {cbo.collaboration_opportunities.length > 2 && (
                        <Badge variant="outline" className="text-xs">
                          +{cbo.collaboration_opportunities.length - 2}
                        </Badge>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1"
                    onClick={() => setSelectedCBO(cbo)}
                  >
                    <Eye className="w-3 h-3 mr-1" />
                    {t('network.view', 'View')}
                  </Button>
                  {cbo.contact_email && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => window.location.href = `mailto:${cbo.contact_email}`}
                    >
                      <MessageSquare className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Shared Resources Section */}
      {sharedResources.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
            <Share2 className="w-5 h-5 text-blue-600" />
            Shared Resources ({sharedResources.length})
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {sharedResources.map(resource => (
              <Card key={resource.id} className="p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 mb-1">{resource.resource_name}</h4>
                    <p className="text-xs text-gray-500">
                      Shared by {resource.shared_by_cbo_name}
                    </p>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {resource.resource_type}
                  </Badge>
                </div>

                {resource.resource_description && (
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                    {resource.resource_description}
                  </p>
                )}

                <div className="flex items-center justify-between text-xs pt-3 border-t">
                  <div className="flex items-center gap-1 text-gray-500">
                    <Calendar className="w-3 h-3" />
                    {formatDate(resource.created_at)}
                  </div>
                  <Badge className="bg-blue-100 text-blue-800">
                    {resource.access_level}
                  </Badge>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* CBO Details Modal */}
      <Dialog open={selectedCBO !== null} onOpenChange={(open) => !open && setSelectedCBO(null)}>
        <DialogContent className="max-w-2xl">
          {selectedCBO && (
            <>
              <DialogHeader>
                <DialogTitle>{selectedCBO.cbo_name}</DialogTitle>
              </DialogHeader>

              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <Card className="p-4 bg-blue-50 border-blue-200">
                    <div className="text-xs text-blue-700 mb-1">{t('network.shared_programs_1', 'Shared Programs')}</div>
                    <div className="text-2xl font-bold text-blue-900">{selectedCBO.shared_programs}</div>
                  </Card>
                  <Card className="p-4 bg-green-50 border-green-200">
                    <div className="text-xs text-green-700 mb-1">{t('network.total_enrollments_2', 'Total Enrollments')}</div>
                    <div className="text-2xl font-bold text-green-900">{selectedCBO.total_enrollments}</div>
                  </Card>
                </div>

                {selectedCBO.service_area && (
                  <div>
                    <Label className="text-sm font-semibold text-gray-700">{t('network.service_area', 'Service Area')}</Label>
                    <p className="text-sm text-gray-900 mt-1">{selectedCBO.service_area}</p>
                  </div>
                )}

                {selectedCBO.collaboration_opportunities && selectedCBO.collaboration_opportunities.length > 0 && (
                  <div>
                    <Label className="text-sm font-semibold text-gray-700">{t('network.collaboration_opportunities', 'Collaboration Opportunities')}</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {selectedCBO.collaboration_opportunities.map((opp, index) => (
                        <Badge key={index} variant="outline">
                          {opp}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {selectedCBO.contact_email && (
                  <div className="pt-4 border-t">
                    <Button
                      className="w-full"
                      onClick={() => window.location.href = `mailto:${selectedCBO.contact_email}`}
                    >
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Contact {selectedCBO.cbo_name}
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CBONetworkView;
